import { FC, ReactNode, useCallback, useMemo, useContext, useState, useEffect } from 'react';
import { FieldType, CustomFieldTypesDoc } from '#types/System.types';
import { fieldDataTypes, SystemFieldTypeId } from '#types';
import { doc, updateDoc, deleteField as firebaseDeleteField } from 'firebase/firestore';
import { cloneDeep, unset } from 'lodash';
import { FieldTypeContext } from './FieldTypeContext';
import { UserContext } from './UserContext';
import { firestore } from '../services/startFirestore';
import { useGet } from '../use/data/useGet';
import { useSet } from '../use/data/useSet';

interface FieldTypeProviderProps {
    children: ReactNode;
}

const DOC_PATH = 'parameters';
const DOC_ID = 'customFieldTypes';

/**
 * Provider component for field type management.
 *
 * Fetches custom field types once from Firestore at app load, merges them
 * with system field types, and provides write methods with optimistic
 * local updates for the rare admin operations that modify field types.
 */
export const FieldTypeProvider: FC<FieldTypeProviderProps> = ({ children }) => {
    const { isLoggedIn, loading: authLoading } = useContext(UserContext);
    const { get } = useGet();
    const { set } = useSet();

    const [data, setData] = useState<CustomFieldTypesDoc | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!isLoggedIn || authLoading) {
            setLoading(false);
            return;
        }

        const fetchFieldTypes = async () => {
            setLoading(true);
            try {
                const result = await get<CustomFieldTypesDoc>(DOC_PATH, DOC_ID);
                if (result) {
                    setData(result);
                } else {
                    await set(DOC_PATH, DOC_ID, {});
                    setData({ docId: DOC_ID } as CustomFieldTypesDoc);
                }
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error(String(err)));
            } finally {
                setLoading(false);
            }
        };

        fetchFieldTypes();
    }, [isLoggedIn, authLoading, get, set]);

    const { fieldTypes, customFieldTypes } = useMemo(() => {
        if (!data || !data.fields) {
            return {
                fieldTypes: fieldDataTypes,
                customFieldTypes: {} as Record<string, FieldType>
            };
        }

        const customTypes: Record<string, FieldType> = {};

        Object.entries(data.fields).forEach(([fieldId, fieldData]) => {
            const baseDataType = fieldDataTypes[fieldData.fieldDataTypeId as SystemFieldTypeId];

            const fieldType: FieldType = {
                ...baseDataType,
                ...fieldData,
                validation: typeof fieldData.validation === 'function' ? fieldData.validation : baseDataType?.validation,
                formatter: typeof fieldData.formatter === 'function' ? fieldData.formatter : baseDataType?.formatter,
                parser: typeof fieldData.parser === 'function' ? fieldData.parser : baseDataType?.parser,
            };

            customTypes[fieldId] = fieldType;
        });

        return {
            fieldTypes: { ...fieldDataTypes, ...customTypes },
            customFieldTypes: customTypes
        };
    }, [data]);

    const setCustomFieldTypes = useCallback((updates: Partial<CustomFieldTypesDoc>) => {
        setData(prev => prev ? {
            ...prev,
            ...updates,
            fields: { ...prev.fields, ...(updates.fields || {}) },
        } : null);
        set(DOC_PATH, DOC_ID, updates).catch(() => {});
    }, [set]);

    const debouncedSetCustomFieldTypes = useCallback((updates: Partial<CustomFieldTypesDoc>) => {
        setCustomFieldTypes(updates);
    }, [setCustomFieldTypes]);

    const deleteFieldType = useCallback(async (fieldId: string) => {
        const docRef = doc(firestore, DOC_PATH, DOC_ID);
        const fieldPath = `fields.${fieldId}`;
        await updateDoc(docRef, { [fieldPath]: firebaseDeleteField() });
        setData(prev => {
            if (!prev) return null;
            const updated = cloneDeep(prev);
            unset(updated, fieldPath);
            return updated;
        });
    }, []);

    const contextValue = useMemo(() => ({
        fieldTypes,
        customFieldTypes,
        isLoading: loading || authLoading,
        error: isLoggedIn ? (error ?? null) : null,
        debouncedSetCustomFieldTypes,
        setCustomFieldTypes,
        deleteFieldType,
    }), [fieldTypes, customFieldTypes, loading, authLoading, isLoggedIn, error, debouncedSetCustomFieldTypes, setCustomFieldTypes, deleteFieldType]);

    return (
        <FieldTypeContext.Provider value={contextValue}>
            {children}
        </FieldTypeContext.Provider>
    );
};
